import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { local } from 'embroider-css-modules';
import HotelChannelPricingTablesCellsMinorChannelsTableTdTableNameCell from 'lumen/components/hotel/channel-pricing/tables/cells/minor-channels-table/td-table-name-cell';
import HotelChannelPricingTablesCellsMinorChannelsTableTdValueCell from 'lumen/components/hotel/channel-pricing/tables/cells/minor-channels-table/td-value-cell';
import type {
  Carate,
  Day,
  Period,
  Restriction,
  RestrictionRecommendation,
} from 'lumen/models';
import type { FormattedOffset } from 'lumen/models/channel-pricing-subscription-config';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  MINOR_CHANNELS_TABLE_ROW_TYPES,
  RESTRICTION_MODEL_CONTEXTS,
} from 'lumen/utils/constants/channel-pricing-constants';
import { DAY_TABLE_SIZES } from 'lumen/utils/constants/general-constants';
import type { RestrictionContext } from 'lumen/utils/constants/restriction-actions';

import styles from './minor-channels-table.module.css';

export interface TdMinorChannelsTableColumnSignature extends CustomEmberTableColumn {
  day: Day;
  daysWithActivePricePush: string[];
  daysWithFailedPricePush: string[];
  daysWithFailedRestrictionPush: Record<string, string[]>;
  hotelLevelRestrictions: Restriction[];
  id: string;
  isPastDay: boolean;
  pricingDataPerPeriod: Record<string, Record<string, Carate>>;
  restrictions: Restriction[];
  restrictionsRecommendations: RestrictionRecommendation[];
}

export interface TdMinorChannelsTableRowSignature extends CustomEmberTableRow {
  channelId?: string;
  channelOffsetsByChannel?: Record<string, FormattedOffset>;
  type: (typeof MINOR_CHANNELS_TABLE_ROW_TYPES)[keyof typeof MINOR_CHANNELS_TABLE_ROW_TYPES];
}

const ROW_PROPERTIES = {
  NAME: 'name',
  TYPE: 'type',
} as const;

interface HotelChannelPricingTablesMinorChannelsTableSignature {
  Args: {
    channelOffsetsByChannel: Record<string, FormattedOffset>;
    data: Period[];
    daysWithActivePricePush: string[];
    daysWithFailedPricePush: string[];
    daysWithFailedRestrictionPush: Record<string, string[]>;
    isLoading: boolean;
    minorChannels: string[];
  };
}

export default class HotelChannelPricingTablesMinorChannelsTable extends Component<HotelChannelPricingTablesMinorChannelsTableSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get columns(): TdMinorChannelsTableColumnSignature[] {
    const columns: Partial<TdMinorChannelsTableColumnSignature>[] = [
      {
        name: this.intl.t('channel_pricing.restrictions'),
        valuePath: 'name',
        textAlign: 'left',
        maxWidth: DAY_TABLE_SIZES.NAME_MAX_WIDTH,
        tdComponent:
          HotelChannelPricingTablesCellsMinorChannelsTableTdTableNameCell,
      },
    ];

    this.args.data.forEach((period) => {
      const restrictions: Restriction[] = this.filterRestrictions<Restriction>(
        period.restrictions,
        RESTRICTION_MODEL_CONTEXTS.CHANNEL_LEVEL,
      );

      const restrictionsRecommendations: RestrictionRecommendation[] =
        this.filterRestrictions<RestrictionRecommendation>(
          period.restrictionRecommendations,
          RESTRICTION_MODEL_CONTEXTS.CHANNEL_LEVEL,
        );

      const hotelLevelRestrictions: Restriction[] = period.restrictions.filter(
        (restriction) =>
          restriction.context === RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
      );

      columns.push({
        id: period.day.id!,
        name: '',
        valuePath: '',
        width: DAY_TABLE_SIZES.VALUE_WIDTH,
        minWidth: DAY_TABLE_SIZES.VALUE_MIN_WIDTH,
        tdComponent:
          HotelChannelPricingTablesCellsMinorChannelsTableTdValueCell,
        day: period.day,
        isPastDay: period.day.isPast,
        tdClassNames: 'channel-pricing-td-no-default-padding',
        pricingDataPerPeriod: this.pricingDataPerPeriod,
        restrictions,
        hotelLevelRestrictions,
        restrictionsRecommendations,
        daysWithActivePricePush: this.args.daysWithActivePricePush,
        daysWithFailedPricePush: this.args.daysWithFailedPricePush,
        daysWithFailedRestrictionPush: this.args.daysWithFailedRestrictionPush,
      });
    });

    return columns as TdMinorChannelsTableColumnSignature[];
  }

  get minorChannelsGroupedById(): Set<string> {
    return new Set(this.args.minorChannels);
  }

  get nrOfLoadingRows(): number {
    return (this.args.minorChannels?.length || 0) + 1; // +1 for the restrictions row
  }

  get pricingDataPerPeriod(): Record<string, Record<string, Carate>> {
    const minorChannelsData: Record<string, Record<string, Carate>> = {};

    this.args.data.forEach((period) => {
      const ratesPerOta: Record<string, Carate> = {};

      period.carates
        .filter((carate) => {
          return (
            // Get own hotel rates, that are not PMS rates (ota === '') and belong to minor channels
            carate.hotelinfo.id ===
              this.hotelAndMarketService.hotel.hotelinfo.id &&
            carate.ota !== '' &&
            this.minorChannelsGroupedById.has(carate.ota)
          );
        })
        .forEach((carate) => {
          ratesPerOta[carate.ota] = carate;
        });

      minorChannelsData[period.day.id!] = ratesPerOta;
    });

    return minorChannelsData;
  }

  get rows(): TdMinorChannelsTableRowSignature[] {
    const rows: TdMinorChannelsTableRowSignature[] = [
      {
        [ROW_PROPERTIES.NAME]: this.intl.t('channel_pricing.restrictions'),
        [ROW_PROPERTIES.TYPE]: MINOR_CHANNELS_TABLE_ROW_TYPES.RESTRICTION,
        className: 'channel-pricing-tr-restrictions',
      },
    ];

    this.args.minorChannels.forEach((minorChannel: string) => {
      rows.push({
        [ROW_PROPERTIES.NAME]: minorChannel,
        [ROW_PROPERTIES.TYPE]: MINOR_CHANNELS_TABLE_ROW_TYPES.MINOR_CHANNEL,
        channelId: minorChannel,
        channelOffsetsByChannel: this.args.channelOffsetsByChannel || {},
      });
    });

    return rows;
  }

  /**
   * Filter Restrictions or RestrictionRecommendations for minor channels
   */
  filterRestrictions<
    T extends { context: RestrictionContext; isForMinorChannel: boolean },
  >(restrictions: T[], context: RestrictionContext): T[] {
    return restrictions.filter(
      (restriction) =>
        restriction.context === context && restriction.isForMinorChannel,
    );
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @fillMode="first-column"
      @isLoading={{@isLoading}}
      @nrOfLoadingRows={{this.nrOfLoadingRows}}
      @rows={{this.rows}}
      @widthConstraint="eq-container"
      class={{local styles "table" "minor-channels-table"}}
      data-test-id="minor-channels-table"
    />
  </template>
}
